import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import { 
  Button, 
  TextField, 
  Typography as Text, 
  Alert, 
  Box, 
  Paper,
  IconButton,
  Collapse,
  Chip,
  Divider
} from '@mui/material';
import { 
  MdCheckCircle, 
  MdError, 
  MdWarning, 
  MdInfo,
  MdClose,
  MdEmail,
  MdMarkEmailRead,
  MdArrowBack,
  MdAccessTime,
} from 'react-icons/md';

import { validateEmail } from '../helper/Validations';
import { Fade } from '../animations';
import * as selectors from '../selectors';
import actions from '../actions';

const styles = theme => ({
  flexWrap: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  actionsWrap: {
    marginTop: '4vh',
  },
  flexCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  countdownText: {
    fontSize: '14px',
    color: '#666',
    minWidth: '60px',
  },
  errorContainer: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  errorPaper: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.error.light}`,
    backgroundColor: theme.palette.error.light + '10',
  },
  warningPaper: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.warning.light}`,
    backgroundColor: theme.palette.warning.light + '10',
  },
  infoPaper: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.info.light}`,
    backgroundColor: theme.palette.info.light + '10',
  },
  successContainer: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  successPaper: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: `2px solid ${theme.palette.success.light}`,
    backgroundColor: theme.palette.success.light + '08',
    textAlign: 'center',
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  errorIconContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  errorIcon: {
    fontSize: '20px',
  },
  errorTitle: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.2,
  },
  errorMessage: {
    fontSize: '13px',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  successIcon: {
    fontSize: '48px',
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  successTitle: {
    fontWeight: 600,
    fontSize: '18px',
    color: theme.palette.success.dark,
    marginBottom: theme.spacing(1),
  },
  successMessage: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  successChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    fontWeight: 500,
  },
  errorChip: {
    fontSize: '11px',
    height: '20px',
    fontWeight: 500,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  emailField: {
    marginTop: theme.spacing(2),
  },
  formContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1)
  },
  backButton: {
    marginBottom: theme.spacing(2),
    alignSelf: 'flex-start',
  },
  titleText: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  successDivider: {
    margin: theme.spacing(2, 0),
    borderColor: theme.palette.success.light,
  },
  successActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
});

function PasswordlessForm({
  classes,
  emailAlreadyExist,
  passwordlessEmail,
  changePasswordlessEmail,
  showPasswordless,
  linkSentToEmail,
  sendLoginLink,
  resetSendLoginLink,
  sendLoginLinkError,
  clearSendLoginLinkError,
}) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [displayError, setDisplayError] = useState(null);
  const [errorTimerId, setErrorTimerId] = useState(null);

  const COOLDOWN_DURATION = 1 * 60; // 5 minutes in seconds
  const STORAGE_KEY = 'passwordlessButtonCooldown';
  const ERROR_VISIBLE_MS = 10000; // Keep error visible for longer

  // Check localStorage on component mount
  useEffect(() => {
    const storedTimestamp = localStorage.getItem(STORAGE_KEY);
    if (storedTimestamp) {
      const elapsed = Math.floor((Date.now() - parseInt(storedTimestamp)) / 1000);
      const remaining = Math.max(0, COOLDOWN_DURATION - elapsed);
      
      if (remaining > 0) {
        setIsButtonDisabled(true);
        setCountdown(remaining);
        startCountdown(remaining);
      } else {
        // Clean up expired timestamp
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (errorTimerId) {
        clearTimeout(errorTimerId);
      }
    };
  }, [intervalId, errorTimerId]);

  // Start cooldown only when linkSentToEmail becomes true
  useEffect(() => {
    if (linkSentToEmail) {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      setIsButtonDisabled(true);
      setCountdown(COOLDOWN_DURATION);
      startCountdown(COOLDOWN_DURATION);
    }
  }, [linkSentToEmail]);

  const startCountdown = useCallback((initialSeconds) => {
    if (intervalId) {
      clearInterval(intervalId);
    }

    const newIntervalId = setInterval(() => {
      setCountdown(prevCountdown => {
        if (prevCountdown <= 1) {
          clearInterval(newIntervalId);
          setIsButtonDisabled(false);
          localStorage.removeItem(STORAGE_KEY);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);

    setIntervalId(newIntervalId);
  }, [intervalId]);

  // Persist the last error locally for a bit longer so it does not disappear immediately
  useEffect(() => {
    if (sendLoginLinkError && sendLoginLinkError.error) {
      setDisplayError(sendLoginLinkError);
      if (errorTimerId) {
        clearTimeout(errorTimerId);
      }
      const id = setTimeout(() => {
        setDisplayError(null);
      }, ERROR_VISIBLE_MS);
      setErrorTimerId(id);
    }
  }, [sendLoginLinkError]);

  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getButtonText = () => {
    if (emailAlreadyExist) {
      return 'EMAIL EXISTS - LOGIN LINK SENT';
    }
    if (isButtonDisabled) {
      return `PLEASE WAIT FOR ${formatCountdown(countdown)} min TO SEND ANOTHER LINK`;
    }
    return 'SEND LOGIN LINK';
  };

  const handleSendLoginLink = () => {
    // Keep currently displayed error visible; we only clear store error after the user dismisses it
    if (!isButtonDisabled && validateEmail(passwordlessEmail)) {
      sendLoginLink(passwordlessEmail);
    }
  };

  const onBackClick = () => {
    showPasswordless(false);
  };

  const getErrorConfig = (error) => {
    if (!error) return null;

    const config = {
      severity: 'error',
      icon: <MdError className={classes.errorIcon} />,
      title: 'Authentication Error',
      chipLabel: 'ERROR',
      chipColor: 'error',
      paperClass: classes.errorPaper,
    };

    if (error.errorType) {
      switch (error.errorType) {
        case 'email_not_found':
          config.severity = 'info';
          config.icon = <MdWarning className={classes.errorIcon} />;
          config.title = 'Email Sent';
          config.chipLabel = 'INFO';
          config.chipColor = 'info';
          config.paperClass = classes.warningPaper;
          break;
        case 'link_already_sent':
          config.severity = 'info';
          config.icon = <MdInfo className={classes.errorIcon} />;
          config.title = 'Link Already Sent';
          config.chipLabel = 'INFO';
          config.chipColor = 'info';
          config.paperClass = classes.infoPaper;
          break;
        case 'status_check_failed':
          config.severity = 'warning';
          config.icon = <MdWarning className={classes.errorIcon} />;
          config.title = 'Status Check Failed';
          config.chipLabel = 'WARNING';
          config.chipColor = 'warning';
          config.paperClass = classes.warningPaper;
          break;
        case 'rate_limit':
          config.severity = 'info';
          config.icon = <MdInfo className={classes.errorIcon} />;
          config.title = 'Rate Limited';
          config.chipLabel = 'INFO';
          config.chipColor = 'info';
          break;
        default:
          break;
      }
    }

    return config;
  };

  const renderErrorAlert = () => {
    if (!displayError || !displayError.error) return null;

    const errorConfig = getErrorConfig(displayError.error);
    if (!errorConfig) return null;

    return (
      <Collapse in={!!displayError} timeout={1200}>
        <Box className={classes.errorContainer}>
          <Paper className={errorConfig.paperClass} elevation={0}>
            <Box className={classes.errorHeader}>
              <Box className={classes.errorIconContainer}>
                {errorConfig.icon}
                <Text className={classes.errorTitle}>
                  {errorConfig.title}
                </Text>
                <Chip 
                  label={errorConfig.chipLabel}
                  color={errorConfig.chipColor}
                  size="small"
                  className={classes.errorChip}
                />
              </Box>
              <IconButton 
                onClick={() => {
                  if (errorTimerId) {
                    clearTimeout(errorTimerId);
                  }
                  setDisplayError(null);
                  if (clearSendLoginLinkError) {
                    clearSendLoginLinkError();
                  }
                }}
                className={classes.closeButton}
                size="small"
              >
                <MdClose />
              </IconButton>
            </Box>
            <Text className={classes.errorMessage}>
              {displayError.error.userFriendlyMessage || 
               displayError.error.message || 
               'An error occurred while sending the login link. Please try again.'}
            </Text>
          </Paper>
        </Box>
      </Collapse>
    );
  };

  const renderSuccessMessage = () => {
    if (!linkSentToEmail) return null;

    return (
      <Collapse in={!!linkSentToEmail}  timeout={500}>
        <Box className={classes.successContainer}>
          <Paper className={classes.successPaper} elevation={0}>
            <MdMarkEmailRead className={classes.successIcon} />
            <Text className={classes.successTitle}>
              Login Link Sent Successfully!
            </Text>
            <Text className={classes.successMessage}>
              We have sent a passwordless link on your email. Please choose the latest one in your inbox to log in.
            </Text>
            <br />
          
            <Divider className={classes.successDivider} />
            
            <Box className={classes.successActions}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={resetSendLoginLink}
                startIcon={<MdArrowBack />}
              >
                Go to Back
              </Button>
             
            </Box>
          </Paper>
        </Box>
      </Collapse>
    );
  };
console.log('isButtonDisabled', isButtonDisabled);
  return !linkSentToEmail ? (
    <Fade>
      <div className={classes.formContainer}>
        <Button 
          variant="text" 
          className={classes.backButton}
          color="primary" 
          onClick={onBackClick}
          startIcon={<MdArrowBack />}
        >
          Back
        </Button>
        
        <Text variant="h6" className={classes.titleText}>
          {emailAlreadyExist ? (
            'If an account exists for this email address, a login link has been sent.'
          ) : (
            'Enter your email address to login'
          )}
        </Text>
        
        {/* Enhanced Error Alert */}
        {renderErrorAlert()}
        
        {/* Success Message */}
        
        <TextField
          placeholder="myemail@provider.com"
          onChange={e => changePasswordlessEmail(e.target.value)}
          value={passwordlessEmail}
          error={!!passwordlessEmail && !validateEmail(passwordlessEmail)}
          readOnly={isButtonDisabled}
          required
          fullWidth
          className={classes.emailField}
          variant="standard"
        />
        
        <div className={classes.actionsWrap}>
          <div className={classes.flexColumn}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendLoginLink}
              disabled={!validateEmail(passwordlessEmail) || isButtonDisabled}
              fullWidth
              size="large"
            >
              {getButtonText()}
            </Button>
          </div>
        </div>
      </div>
    </Fade>
  ) : (
    renderSuccessMessage()
  );
}

export default connect(
  (state, _) => ({
    sendLoginLinkError: selectors.getSendLoginLinkError(state),
  }),
  (dispatch, _) => ({
    clearSendLoginLinkError: () => dispatch(actions.app.temporary.sendLoginLinkFailure({ error: null })),
  }),
)(withStyles(styles)(PasswordlessForm));
